import React, { useEffect, useMemo, useState } from 'react';
import { format, startOfWeek, endOfWeek, addDays, isSameDay, startOfMonth, getWeeksInMonth, addWeeks, isSameMonth, isThisWeek, parseISO, isValid, isBefore, startOfToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import Select from 'react-select';
import { useSelectStyles } from '../../utils/selectStyles';
import { obfuscateUsername } from '../../pages/VisitsViewer';
import {
  VISIT_STATUS,
  selectableStatuses,
  statusLabel,
  statusColor,
  statusRank,
} from '../../constants/visitsClassification';
import {
  hasUniversSplit,
  universOf,
  universLabel,
  universColorMap,
} from '../../constants/visitsUnivers';
import UniversChip from './UniversChip';

function VisitsCalendar({ visits, startDate, endDate, obfuscate, currentUser, showOutOfScope = false }) {
  const selectStyles = useSelectStyles();
  const isAdmin = currentUser?.isadmin;

  // Options du filtre de statut, dérivées du vocabulaire partagé.
  //
  // This component used to keep its own parallel vocabulary (`visitType`), in which
  // 'missed' meant "Non effectuée" and 'overdue' meant "Manquée" — the opposite way
  // round from the domain status now named MISSED. That mapping is gone: the filter,
  // the sort and the colors all read `visit.status` directly.
  const statusOptions = selectableStatuses({ isAdmin });

  // The payload is one row per (visit × univers), so a Blanc+Brun visit draws TWO
  // cards with the same store name. Without the univers they read as a duplicate.
  // Only shown when the project actually splits — a lone "Sans univers" on every
  // card is noise.
  const showUnivers = useMemo(() => hasUniversSplit(visits), [visits]);
  const universColors = useMemo(
    () => universColorMap(visits, document.documentElement.classList.contains('dark')),
    [visits]
  );

  // Capped so the store name — the card's primary information — always keeps room.
  const universChip = (visit) => (showUnivers ? (
    <UniversChip
      label={universLabel(universOf(visit))}
      color={universColors.get(universOf(visit))}
      className="max-w-[45%] text-[10px] leading-4"
    />
  ) : null);

  // État pour suivre les semaines réduites/dépliées
  const [collapsedWeeks, setCollapsedWeeks] = useState({});
  const [viewMode, setViewMode] = useState('week');
  // Quelle carte vient d'être copiée, pour un flash bref plutôt qu'une copie muette.
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    if (!copiedKey) return undefined;
    const timer = setTimeout(() => setCopiedKey(null), 1200);
    return () => clearTimeout(timer);
  }, [copiedKey]);
  // Filtre de statut: par défaut tout est coché SAUF les supprimées (qui polluent ;
  // l'admin les ajoute manuellement s'il veut les voir).
  const [selectedStatuses, setSelectedStatuses] = useState(
    () => statusOptions.filter(o => showOutOfScope || o.value !== VISIT_STATUS.CANCELLED)
  );

  // The header checkbox governs which rows reach this component; this filter governs
  // which of them are drawn. Left unsynced, an admin who ticked "afficher les visites
  // hors périmètre" still saw no deleted visit here and reasonably concluded the
  // checkbox did nothing. Flipping it now reseeds the status selection.
  useEffect(() => {
    setSelectedStatuses(statusOptions.filter(o => showOutOfScope || o.value !== VISIT_STATUS.CANCELLED));
    // statusOptions is rebuilt on every render; only the toggle should retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOutOfScope]);

  // On utilise la date d'aujourd'hui pour comparer les dates
  const today = startOfToday();

  // Normaliser les visites en utilisant le status plutôt que les dates
  const normalizedVisits = useMemo(() => {
    return visits.map(visit => {
      // Convertir les dates en objets Date, mais ne pas les utiliser pour déterminer le statut
      let plannedDate = null;
      if (visit.plannedStart) {
        plannedDate = typeof visit.plannedStart === 'string'
          ? parseISO(visit.plannedStart)
          : visit.plannedStart;

        if (!isValid(plannedDate)) {
          plannedDate = null;
        }
      }

      let actualDate = null;
      if (visit.date) {
        actualDate = typeof visit.date === 'string'
          ? parseISO(visit.date)
          : visit.date;

        if (!isValid(actualDate)) {
          actualDate = null;
        }
      }

      // No color carried per visit any more: getVisitCardStyle() derives it from
      // `status` through the shared palette. Leaving the key here would have put the
      // imported statusColor FUNCTION on every visit, shadowing the color string the
      // payload already carries.
      return {
        ...visit,
        plannedStart: plannedDate,
        date: actualDate,
      };
    });
  }, [visits, today]);

  // Filtre de base: les non-admins ne voient que les vraies visites effectuées/prévues
  // (jamais les supprimées, spéciales ou non effectuées). Les admins voient tout, badgé.
  const baseVisits = useMemo(() => {
    if (isAdmin) return normalizedVisits;
    return normalizedVisits.filter(v =>
      !v.specialType && (v.status === VISIT_STATUS.COMPLETED || v.status === VISIT_STATUS.PLANNED)
    );
  }, [normalizedVisits, isAdmin]);

  // Filtrer les visites en fonction des statuts sélectionnés (admins uniquement)
  const filteredVisits = useMemo(() => {
    if (!isAdmin) {
      return baseVisits;
    }

    if (!selectedStatuses || selectedStatuses.length === 0) {
      return baseVisits;
    }

    const selectedStatusValues = selectedStatuses.map(s => s.value);
    return baseVisits.filter(visit => selectedStatusValues.includes(visit.status));
  }, [baseVisits, selectedStatuses, isAdmin]);

  // Fonction de tri des visites qui peut être utilisée dans les deux vues
  function sortVisitsByStatusAndDate(visits) {
    return [...visits].sort((a, b) => {
      // 1. Trier d'abord par statut. statusRank() ranks an unrecognized status last
      // instead of returning undefined — the old table here omitted "Manquée"
      // entirely, so `order[status] - order[status]` was NaN and those visits
      // sorted arbitrarily.
      const statusDiff = statusRank(a.status) - statusRank(b.status);
      if (statusDiff !== 0) return statusDiff;

      // 2. Si même statut, trier par date
      const aDate = a.date || a.plannedStart;
      const bDate = b.date || b.plannedStart;
      if (aDate && bDate) return aDate - bDate;
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    });
  }

  // Génération du calendrier en fonction du mode d'affichage
  const calendarData = useMemo(() => {
    if (viewMode === 'week') {
      const weeks = generateWeeklyCalendar(filteredVisits, startDate, endDate);

      // Initialiser l'état de réduction des semaines uniquement au premier rendu
      if (Object.keys(collapsedWeeks).length === 0) {
        const initialCollapsedState = {};

        // Inverser l'ordre pour que les plus récentes soient en premier
        const reversedWeeks = [...weeks].reverse();

        // Initialiser l'état de collapse pour chaque semaine
        reversedWeeks.forEach((week, index) => {
          // Vérifier si cette semaine contient le jour courant
          const isCurrentWeek = week.some(day => isThisWeek(day.date, { locale: fr }));

          // Par défaut toutes les semaines sont réduites SAUF la semaine courante
          initialCollapsedState[index] = !isCurrentWeek;
        });

        setCollapsedWeeks(initialCollapsedState);
      }

      // Inverser l'ordre des semaines (de la plus récente à la plus ancienne)
      return [...weeks].reverse();
    } else {
      return generateMonthlyCalendar(filteredVisits, startDate, endDate);
    }
  }, [filteredVisits, viewMode, startDate, endDate, collapsedWeeks]);

  // Génération d'un calendrier hebdomadaire
  function generateWeeklyCalendar(visits, start, end) {
    const weeks = [];
    let currentDate = startOfWeek(start, { locale: fr });
    const endDate = endOfWeek(end, { locale: fr });

    while (currentDate <= endDate) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        const day = addDays(currentDate, i);

        // Filtrer les visites pour cette journée
        const dayVisits = visits.filter(visit => {
          const matchesActualDate = visit.date && isSameDay(visit.date, day);
          const matchesPlannedDate = visit.plannedStart && isSameDay(visit.plannedStart, day);
          return matchesActualDate || matchesPlannedDate;
        });

        // Trier les visites du jour
        const sortedDayVisits = sortVisitsByStatusAndDate(dayVisits);

        week.push({
          date: day,
          visits: sortedDayVisits
        });
      }
      weeks.push(week);
      currentDate = addDays(currentDate, 7);
    }

    return weeks;
  }

  // Génération d'un calendrier mensuel
  function generateMonthlyCalendar(visits, start, end) {
    const months = [];
    let currentMonth = startOfMonth(start);

    while (currentMonth <= end) {
      const firstDayOfMonth = startOfMonth(currentMonth);
      // Same locale as the startOfWeek() below, or the grid is sized Sunday-first
      // and filled Monday-first: a month starting on a Sunday runs out of rows and
      // silently drops its last days.
      const numberOfWeeks = getWeeksInMonth(currentMonth, { locale: fr });

      const month = {
        name: format(currentMonth, 'MMMM yyyy', { locale: fr }),
        days: []
      };

      let day = startOfWeek(firstDayOfMonth, { locale: fr });

      for (let i = 0; i < (numberOfWeeks * 7); i++) {
        const currentDay = addDays(day, i);

        // Filtrer les visites pour cette journée
        const dayVisits = visits.filter(visit => {
          const matchesActualDate = visit.date && isSameDay(visit.date, currentDay);
          const matchesPlannedDate = visit.plannedStart && isSameDay(visit.plannedStart, currentDay);
          return matchesActualDate || matchesPlannedDate;
        });

        // Trier les visites du jour
        const sortedDayVisits = sortVisitsByStatusAndDate(dayVisits);

        month.days.push({
          date: currentDay,
          isCurrentMonth: isSameMonth(currentDay, currentMonth),
          visits: sortedDayVisits
        });
      }

      months.push(month);
      currentMonth = addDays(addWeeks(firstDayOfMonth, numberOfWeeks), 1);
    }

    return months;
  }

  // Formatage durée en minutes
  function formatDuration(minutes) {
    if (!minutes) return "N/A";
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}m` : ''}`;
  }

  // Tooltip compact: tout le détail (user, statut, durée) au survol, pour ne garder
  // que le nom du magasin dans la carte. Les admins y voient en plus l'id de la
  // visite dans son système source — c'est la clé pour la retrouver dans le DWH.
  const getVisitTitle = (visit) => {
    const duration = visit.actualDurationMinutes || visit.plannedDurationMinutes;
    const parts = [
      [visit.interventionPoint, showUnivers && universLabel(universOf(visit))].filter(Boolean).join(' · '),
      obfuscate ? obfuscateUsername(visit.username) : visit.username,
      `${statusLabel(visit.status)}${duration ? ` · ${formatDuration(duration)}` : ''}`,
    ];
    if (visit.maxDurationReached) parts.push('Durée écrêtée à 4h');
    if (isAdmin && visit.visitId) {
      parts.push([visit.sourceSystem, visit.visitId].filter(Boolean).join(' '));
    }
    return parts.filter(Boolean).join(' — ');
  };

  // Clic sur une carte = l'infobulle dans le presse-papier, pour la coller dans un
  // ticket ou une requête. Admin only, comme l'id qu'elle contient.
  const rowKey = (visit) => visit.universKey || visit.visitId;

  const copyVisit = (visit) => {
    // Absent hors contexte sécurisé, et peut être refusé par l'utilisateur : une
    // copie qui échoue ne doit ni mentir sur le succès ni faire tomber la vue.
    if (!navigator.clipboard?.writeText) return;
    try {
      const done = navigator.clipboard.writeText(getVisitTitle(visit));
      // Le flash n'est promis qu'à une vraie promesse tenue : sans elle on n'a
      // aucune preuve que la copie a eu lieu, et annoncer un succès serait faux.
      if (typeof done?.then !== 'function') return;
      done.then(() => setCopiedKey(rowKey(visit))).catch(() => {});
    } catch {
      // Un writeText qui lève ne doit pas remonter depuis un gestionnaire de clic.
    }
  };

  // Une seule définition de l'affordance pour les deux dispositions de carte.
  const copyAffordance = (visit) => {
    if (!isAdmin) return { className: 'cursor-default', props: {} };
    const copied = copiedKey === rowKey(visit);
    return {
      className: `cursor-pointer ${copied ? 'ring-2 ring-blue-500' : ''}`,
      props: {
        role: 'button',
        tabIndex: 0,
        'aria-label': `Copier le détail de la visite ${visit.interventionPoint}`,
        onClick: () => copyVisit(visit),
        onKeyDown: (e) => {
          if (e.key !== 'Enter' && e.key !== ' ') return;
          e.preventDefault();
          copyVisit(visit);
        },
      },
    };
  };

  // Toggle l'état réduit/déplié d'une semaine
  const toggleWeekCollapse = (weekIndex) => {
    setCollapsedWeeks(prev => ({
      ...prev,
      [weekIndex]: !prev[weekIndex]
    }));
  };

  // Card background/border, both from the one status color. The '20' suffix is the
  // alpha channel of an 8-digit hex color.
  const getVisitCardStyle = (visit) => {
    const borderColor = statusColor(visit.status);
    return {
      backgroundColor: `${borderColor}20`,
      borderLeft: `3px solid ${borderColor}`
    };
  };

  return (
    <div>
      {/* Barre d'outils avec la sélection du mode d'affichage et le filtre de statut */}
      <div className="flex flex-wrap justify-end items-center mb-4 gap-4">
        {/* Filtre de statut */}
        {isAdmin && (
          <div className="">
            <Select
              isMulti
              name="statuses"
              options={statusOptions}
              className="basic-multi-select"
              classNamePrefix="select"
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              placeholder="Filtrer par statut..."
              noOptionsMessage={() => "Aucun statut disponible"}
              isClearable={false}
              closeMenuOnSelect={false}
              styles={selectStyles}
            />
          </div>
        )}

        {/* Sélection du mode d'affichage */}
        <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 h-[38px]">
          <button
            className={`flex items-center justify-center px-4 py-2 text-sm font-medium ${
              viewMode === 'week'
                ? 'bg-blue-500 text-white border-blue-600'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
            onClick={() => setViewMode('week')}
          >
            Semaine
          </button>
          <button
            className={`flex items-center justify-center px-4 py-2 text-sm font-medium ${
              viewMode === 'month'
                ? 'bg-blue-500 text-white border-blue-600'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
            onClick={() => setViewMode('month')}
          >
            Mois
          </button>
        </div>
      </div>

      {/* Légende des statuts */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-2"></span>
          <span className="text-sm text-gray-700 dark:text-gray-300">Effectuée</span>
        </div>
        <div className="flex items-center">
          <span className="inline-block w-3 h-3 rounded-full bg-blue-500 mr-2"></span>
          <span className="text-sm text-gray-700 dark:text-gray-300">Prévue</span>
        </div>
        
        {/* Admin only */}
        {isAdmin && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-3 px-3 py-2 border border-purple-300 dark:border-purple-600 rounded-lg bg-purple-50 dark:bg-purple-900/20">
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-amber-500 mr-2"></span>
                <span className="text-sm text-gray-700 dark:text-gray-300">Non effectuée</span>
              </div>
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-orange-500 mr-2"></span>
                <span className="text-sm text-gray-700 dark:text-gray-300">Manquée</span>
              </div>
              <div className="flex items-center">
                <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-2"></span>
                <span className="text-sm text-gray-700 dark:text-gray-300">Supprimée</span>
              </div>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-500" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2L4 6v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V6l-8-4z"/>
              <text x="13" y="16" textAnchor="middle" className="text-xs font-bold fill-white">A</text>
            </svg>
          </div>
        )}
      </div>

      {/* Contenu du calendrier */}
      <div className="overflow-auto">
        {viewMode === 'week' ? (
          <div className="min-w-full">
            {calendarData.map((week, weekIndex) => {
              const firstDayOfWeek = format(week[0].date, 'dd MMMM', { locale: fr });
              const lastDayOfWeek = format(week[6].date, 'dd MMMM yyyy', { locale: fr });

              // Vérifier si cette semaine est la semaine courante
              const isCurrentWeek = week.some(day => isThisWeek(day.date, { locale: fr }));

              // Calculer le nombre total de visites dans cette semaine
              const totalVisitsInWeek = week.reduce((sum, day) => sum + day.visits.length, 0);

              // Ne garder que les jours qui ont des visites: ils prennent ainsi toute
              // la largeur disponible (les jours vides, ex. week-ends, sont masqués).
              const daysWithVisits = week.filter(day => day.visits.length > 0);

              return (
                <div key={weekIndex} className={`mb-6 border rounded-lg overflow-hidden
                  ${isCurrentWeek
                    ? 'border-blue-400 dark:border-blue-600'
                    : 'border-gray-200 dark:border-gray-700'}`}
                >
                  <div
                    className={`flex justify-between items-center p-3 cursor-pointer ${
                      isCurrentWeek
                        ? 'bg-blue-50 dark:bg-blue-900/20'
                        : 'bg-gray-50 dark:bg-gray-800'
                    }`}
                    onClick={() => toggleWeekCollapse(weekIndex)}
                  >
                    <h3 className="text-lg font-semibold dark:text-white flex items-center">
                      <span className="transform transition-transform duration-200 inline-block mr-2">
                        {collapsedWeeks[weekIndex] ? '►' : '▼'}
                      </span>
                      Semaine du {firstDayOfWeek} au {lastDayOfWeek}
                      {isCurrentWeek && (
                        <span className="ml-2 text-xs font-normal px-2 py-1 bg-blue-500 text-white rounded-full">
                          Semaine courante
                        </span>
                      )}
                    </h3>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {totalVisitsInWeek} visite{totalVisitsInWeek !== 1 && 's'}
                    </div>
                  </div>

                  {!collapsedWeeks[weekIndex] && (
                    daysWithVisits.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">Aucune visite cette semaine</div>
                    ) : (
                      <div className="flex flex-wrap gap-2 p-3">
                        {daysWithVisits.map((day, dayIndex) => (
                          <div key={dayIndex} className="border rounded-lg p-2 flex-1 min-w-[200px] dark:border-gray-700">
                            <div className="text-center mb-2 font-semibold dark:text-gray-300 capitalize">
                              {format(day.date, 'EEEE dd', { locale: fr })}
                              <span className="ml-1 text-gray-500 dark:text-gray-400">
                                ({day.visits.length})
                              </span>
                            </div>
                            <div className="space-y-1">
                              {day.visits.map((visit, visitIndex) => {
                                const copy = copyAffordance(visit);
                                return (
                                <div
                                  key={visitIndex}
                                  {...copy.props}
                                  className={`px-2 py-1 rounded text-xs flex items-center gap-1.5 ${copy.className}`}
                                  style={getVisitCardStyle(visit)}
                                  title={getVisitTitle(visit)}
                                >
                                  {universChip(visit)}
                                  <span className="truncate font-medium">{visit.interventionPoint}</span>
                                  {visit.maxDurationReached && (
                                    <span className="ml-auto flex-shrink-0 text-amber-600 dark:text-amber-400" title="Durée écrêtée à 4h">⚠️</span>
                                  )}
                                </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-8">
            {calendarData.map((month, monthIndex) => (
              <div key={monthIndex} className="border rounded-lg p-4 dark:border-gray-700">
                <h3 className="text-xl font-semibold mb-4 text-center dark:text-white capitalize">
                  {month.name}
                </h3>
                <div className="grid grid-cols-7 gap-1">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                    <div key={day} className="text-center font-medium p-2 text-sm dark:text-gray-300">
                      {day}
                    </div>
                  ))}

                  {month.days.map((day, dayIndex) => (
                    <div
                      key={dayIndex}
                      className={`border p-1 min-h-24 h-24 overflow-auto text-sm
                        ${day.isCurrentMonth ? 'bg-white dark:bg-gray-800' : 'bg-gray-100 dark:bg-gray-900 text-gray-400 dark:text-gray-600'}
                        ${isSameDay(day.date, new Date()) ? 'ring-2 ring-blue-500' : ''}
                        dark:border-gray-700`}
                    >
                      <div className="text-right mb-1">
                        {format(day.date, 'd')}
                        {day.visits.length > 0 && (
                          <span className={`ml-1 ${day.isCurrentMonth ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-600'}`}>
                            ({day.visits.length})
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {day.visits
                          .sort((a, b) => statusRank(a.status) - statusRank(b.status))
                          .map((visit, visitIndex) => {
                            const style = getVisitCardStyle(visit);
                            const copy = copyAffordance(visit);
                            return (
                              <div
                                key={visitIndex}
                                {...copy.props}
                                className={`p-1 rounded text-xs truncate flex items-center gap-1 ${copy.className}`}
                                style={{
                                  backgroundColor: style.backgroundColor,
                                  borderLeft: style.borderLeft
                                }}
                                title={getVisitTitle(visit)}
                              >
                                {universChip(visit)}
                                <span className="truncate">{visit.interventionPoint}</span>
                                {visit.maxDurationReached && (
                                  <span className="ml-auto text-amber-600 dark:text-amber-400 flex-shrink-0">⚠️</span>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VisitsCalendar;